import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { 
  HeartIcon, 
  ScaleIcon, 
  UserGroupIcon, 
  DocumentTextIcon,
  EnvelopeIcon,
  PaperAirplaneIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';

const PricingPage: React.FC = () => {
  const { t } = useTranslation();

  const mailingServices = [
    {
      id: 'lawsuit-filing',
      name: t('pricing.mailing.lawsuitFiling.name', 'Lawsuit Filing & Service'),
      description: t('pricing.mailing.lawsuitFiling.description', 'Professional filing and service of legal documents to courts and defendants'),
      services: [
        t('pricing.mailing.lawsuitFiling.service1', 'Court filing fees (varies by jurisdiction)'),
        t('pricing.mailing.lawsuitFiling.service2', 'Certified mail service to defendants'),
        t('pricing.mailing.lawsuitFiling.service3', 'Process server fees (when required)'),
        t('pricing.mailing.lawsuitFiling.service4', 'Return receipt tracking'),
      ],
      pricing: t('pricing.mailing.lawsuitFiling.pricing', 'Cost varies by location and case complexity'),
      icon: ScaleIcon
    },
    {
      id: 'document-delivery',
      name: t('pricing.mailing.documentDelivery.name', 'Document Delivery Service'),
      description: t('pricing.mailing.documentDelivery.description', 'Secure postal delivery of important legal documents'),
      services: [
        t('pricing.mailing.documentDelivery.service1', 'Certified mail with tracking'),
        t('pricing.mailing.documentDelivery.service2', 'Priority handling'),
        t('pricing.mailing.documentDelivery.service3', 'Return receipt confirmation'),
        t('pricing.mailing.documentDelivery.service4', 'International delivery available'),
      ],
      pricing: t('pricing.mailing.documentDelivery.pricing', 'Starting from $15 per delivery'),
      icon: EnvelopeIcon
    },
    {
      id: 'premium-ai',
      name: t('pricing.mailing.premiumAI.name', 'Premium AI Models'),
      description: t('pricing.mailing.premiumAI.description', 'Access to advanced AI models for enhanced legal analysis and document generation'),
      services: [
        t('pricing.mailing.premiumAI.service1', 'GPT-4 and Claude-3 advanced models'),
        t('pricing.mailing.premiumAI.service2', 'Enhanced document analysis capabilities'),
        t('pricing.mailing.premiumAI.service3', 'Priority processing speed'),
        t('pricing.mailing.premiumAI.service4', 'Advanced legal reasoning'),
      ],
      pricing: t('pricing.mailing.premiumAI.pricing', 'Optional upgrade based on usage'),
      icon: SparklesIcon
    }
  ];

  const platformFeatures = [
    {
      icon: HeartIcon,
      title: t('pricing.platform.justice.title', 'Justice for All'),
      description: t('pricing.platform.justice.description', 'Our mission is to understand patterns of injustice, not to profit from those seeking justice.')
    },
    {
      icon: UserGroupIcon,
      title: t('pricing.platform.collective.title', 'Power of Many'),
      description: t('pricing.platform.collective.description', 'We harness collective action to identify widespread injustices that need correction.')
    },
    {
      icon: DocumentTextIcon,
      title: t('pricing.platform.tools.title', 'Complete Legal Tools'),
      description: t('pricing.platform.tools.description', 'AI-powered document generation, case analysis, and legal guidance - all completely free.')
    },
    {
      icon: PaperAirplaneIcon,
      title: t('pricing.platform.global.title', 'Global Access'),
      description: t('pricing.platform.global.description', 'Multi-language support and international legal frameworks - accessible to everyone.')
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            {t('pricing.title', 'Our Platform is Completely Free')}
          </h1>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            {t('pricing.subtitle', 'We believe justice should be accessible to everyone. Our AI-powered legal platform is entirely free because our goal is not to make money from those seeking justice, but to understand if widespread injustice exists that should be corrected through the power of collective action.')}
          </p>
        </div>


        {/* Mission Statement */}
        <div className="bg-gradient-to-r from-blue-600 to-cyan-600 rounded-lg p-8 mb-16 text-white text-center">
          <h2 className="text-2xl font-bold mb-4">
            {t('pricing.mission.title', 'Our Mission')}
          </h2>
          <p className="text-lg opacity-95 max-w-3xl mx-auto">
            {t('pricing.mission.description', 'We are not here to profit from your pursuit of justice. Instead, we aim to identify patterns of injustice affecting many people and use the collective power of communities to create meaningful change. When many voices unite, we can correct systemic problems that individual action cannot address.')}
          </p>
        </div>

        {/* Free Platform Features */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            {t('pricing.platform.title', 'Everything You Need - Completely Free')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {platformFeatures.map((feature, index) => (
              <div key={index} className="bg-white rounded-lg p-6 shadow-sm border border-gray-200 text-center">
                <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-600 text-sm">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* What's Included for Free */}
        <div className="bg-white rounded-lg shadow-sm p-8 mb-16">
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-8">
            {t('pricing.included.title', 'What\'s Included at No Cost')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900 text-lg">
                {t('pricing.included.legal.title', 'Legal Tools')}
              </h3>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-700">{t('pricing.included.legal.feature1', 'AI-powered case analysis')}</span>
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-700">{t('pricing.included.legal.feature2', 'Legal document generation')}</span>
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-700">{t('pricing.included.legal.feature3', 'Unlimited document templates')}</span>
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-700">{t('pricing.included.legal.feature4', 'Multi-language support')}</span>
                </li>
              </ul>
            </div>
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900 text-lg">
                {t('pricing.included.platform.title', 'Platform Features')}
              </h3>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-700">{t('pricing.included.platform.feature1', 'Class action discovery')}</span>
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-700">{t('pricing.included.platform.feature2', 'Unlimited case management')}</span>
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-700">{t('pricing.included.platform.feature3', 'Community collaboration')}</span>
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-700">{t('pricing.included.platform.feature4', 'Progress tracking')}</span>
                </li>
              </ul>
            </div>
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900 text-lg">
                {t('pricing.included.support.title', 'Support & Resources')}
              </h3>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-700">{t('pricing.included.support.feature1', 'Comprehensive help center')}</span>
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-700">{t('pricing.included.support.feature2', 'Community support forums')}</span>
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-700">{t('pricing.included.support.feature3', 'Legal resource library')}</span>
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-700">{t('pricing.included.support.feature4', 'Regular platform updates')}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Only Paid Services - Mailing */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">
            {t('pricing.mailing.title', 'The Only Costs: Physical Document Services')}
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
            {t('pricing.mailing.subtitle', 'We only charge for actual postal and delivery services when you need physical documents filed or mailed. These costs cover third-party services like court fees, certified mail, and process servers.')}
          </p>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {mailingServices.map((service) => (
              <div key={service.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
                <div className="flex items-start mb-6">
                  <div className="bg-amber-100 rounded-full w-12 h-12 flex items-center justify-center mr-4 flex-shrink-0">
                    <service.icon className="h-6 w-6 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">{service.name}</h3>
                    <p className="text-gray-600">{service.description}</p>
                  </div>
                </div>
                
                <ul className="space-y-3 mb-6">
                  {service.services.map((item, index) => (
                    <li key={index} className="flex items-start">
                      <svg className="h-5 w-5 text-amber-500 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="text-gray-700 text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
                
                <div className="bg-amber-50 rounded-lg p-4">
                  <p className="text-amber-800 font-medium text-sm">{service.pricing}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Transparency Section */}
        <div className="bg-blue-50 rounded-lg p-8 mb-16">
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-6">
            {t('pricing.transparency.title', 'Complete Transparency')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">
                {t('pricing.transparency.noHidden.title', 'No Hidden Fees')}
              </h3>
              <p className="text-gray-600 text-sm">
                {t('pricing.transparency.noHidden.description', 'Every cost is clearly explained upfront. No surprises, no subscription traps.')}
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">
                {t('pricing.transparency.actualCost.title', 'Actual Cost Only')}
              </h3>
              <p className="text-gray-600 text-sm">
                {t('pricing.transparency.actualCost.description', 'We only charge what third-party services cost us. No markup, no profit on these services.')}
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">
                {t('pricing.transparency.optional.title', 'Completely Optional')}
              </h3>
              <p className="text-gray-600 text-sm">
                {t('pricing.transparency.optional.description', 'You can use our platform entirely without any payment. Mailing services are only if you choose them.')}
              </p>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="bg-white rounded-lg shadow-sm p-8 mb-16">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-8">
            {t('pricing.faq.title', 'Frequently Asked Questions')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {t('pricing.faq.q1', 'Why is the platform completely free?')}
              </h3>
              <p className="text-gray-600">
                {t('pricing.faq.a1', 'Our goal is to understand patterns of injustice affecting many people and use collective action to create change. We are not here to profit from your pursuit of justice.')}
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {t('pricing.faq.q2', 'What are the mailing costs for?')}
              </h3>
              <p className="text-gray-600">
                {t('pricing.faq.a2', 'These cover actual third-party services like court filing fees, certified mail, process servers, and postal delivery. We charge exactly what these services cost us.')}
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {t('pricing.faq.q3', 'Can I file a lawsuit without any costs?')}
              </h3>
              <p className="text-gray-600">
                {t('pricing.faq.a3', 'Yes! You can prepare, review, and even download all legal documents for free. You only pay if you want us to physically file or mail documents for you.')}
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {t('pricing.faq.q4', 'Will this always be free?')}
              </h3>
              <p className="text-gray-600">
                {t('pricing.faq.a4', 'Yes. Our mission-driven approach means the platform will always be free. We are committed to accessible justice for everyone.')}
              </p>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="bg-gradient-to-r from-cyan-600 to-blue-600 rounded-lg p-8 text-center text-white">
          <h2 className="text-2xl font-bold mb-4">
            {t('pricing.cta.title', 'Ready to Seek Justice?')}
          </h2>
          <p className="text-lg mb-6 opacity-90">
            {t('pricing.cta.description', 'Join thousands of people using our free platform to understand their legal options and connect with others facing similar challenges.')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/register"
              className="bg-white text-cyan-600 px-6 py-3 rounded-md font-medium hover:bg-gray-100 transition-colors duration-200"
            >
              {t('pricing.cta.getStarted', 'Get Started Free')}
            </Link>
            <Link
              to="/about"
              className="border-2 border-white text-white px-6 py-3 rounded-md font-medium hover:bg-white hover:text-cyan-600 transition-colors duration-200"
            >
              {t('pricing.cta.learnMore', 'Learn About Our Mission')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;